<template>
  <div
    class="alice-row"
    :class="{ 'alice-row--compact': density === 'compact' }"
    role="button"
    tabindex="0"
    @click="$emit('open', project)"
    @keydown.enter="$emit('open', project)"
  >
    <span class="alice-row__stripe" aria-hidden="true" />

    <div class="alice-row__title-cell">
      <div class="alice-row__title">{{ project.title }}</div>
      <div v-if="metaLine" class="alice-row__meta">{{ metaLine }}</div>
    </div>

    <div class="alice-row__cell-client">
      <ClientChip :name="project.client" :color="project.clientColor" />
    </div>

    <div>
      <StatusTag :status="project.status" />
    </div>

    <div class="alice-row__owner">
      <Avatar
        v-if="project.owner"
        :name="project.owner.name"
        :photo="project.owner.photo"
        :color="project.owner.color"
        :size="26"
      />
      <Avatar
        v-else
        name=""
        initials="—"
        color="var(--vp-grijs-20)"
        :size="26"
      />
      <span class="alice-row__owner-name">{{ ownerFirstName }}</span>
    </div>

    <div class="alice-row__cell-created">
      <div>{{ createdFull }}</div>
      <div class="alice-row__rel">{{ createdRel }}</div>
    </div>

    <div class="alice-row__actions">
      <button
        class="alice-row__action"
        title="Open"
        @click.stop="$emit('open', project)"
      >
        <Icon name="arrow-right" :size="16" />
      </button>
    </div>
  </div>
</template>

<script>
import Avatar from "./Avatar.vue";
import ClientChip from "./ClientChip.vue";
import StatusTag from "./StatusTag.vue";
import Icon from "./Icon.vue";
import { fmtDate, relTime } from "./normalize";

export default {
  name: "AliceProjectRow",
  components: { Avatar, ClientChip, StatusTag, Icon },
  props: {
    project: { type: Object, required: true },
    density: { type: String, default: "cozy" },
  },
  emits: ["open"],
  computed: {
    metaLine() {
      const parts = [];
      if (this.project.method) parts.push(this.project.method);
      if (typeof this.project.n === "number")
        parts.push(`n=${this.project.n.toLocaleString("en-GB")}`);
      return parts.join(" · ");
    },
    ownerFirstName() {
      if (!this.project.owner) return "—";
      return this.project.owner.name.split(" ")[0];
    },
    createdFull() {
      return fmtDate(this.project.created);
    },
    createdRel() {
      return relTime(this.project.created);
    },
  },
};
</script>

<style scoped>
.alice-row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 2.4fr) 200px 140px 120px 130px 50px;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-hover);
}
.alice-row--compact {
  padding: 10px 24px;
}
.alice-row:hover,
.alice-row:focus-visible {
  background: var(--bg-muted);
  outline: none;
}
.alice-row__stripe {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
}
.alice-row:hover .alice-row__stripe,
.alice-row:focus-visible .alice-row__stripe {
  background: var(--accent-100);
}

.alice-row__title-cell {
  min-width: 0;
}
.alice-row__title {
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-row__meta {
  margin-top: 2px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

.alice-row__owner {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.alice-row__owner-name {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alice-row__cell-created {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
}
.alice-row__rel {
  font-size: 11px;
  color: var(--fg-3);
}

.alice-row__actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}
.alice-row__action {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
  border-radius: 0;
  transition: border-color 0.15s ease;
}
.alice-row:hover .alice-row__action,
.alice-row:focus-visible .alice-row__action {
  border-color: var(--vp-black);
}

@media (max-width: 1279px) {
  .alice-row {
    grid-template-columns: minmax(0, 2.4fr) 140px 120px 50px;
  }
  .alice-row__cell-client,
  .alice-row__cell-created {
    display: none;
  }
}
</style>
